import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, Mail, Play, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function fill(s, vars) {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, v), s);
}

function DeleteBadge({ t, days }) {
  const urgent = days <= 7;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
        urgent ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
      }`}
    >
      <Trash2 className="w-3 h-3" />
      {days === 0 ? t("drip.delete_due") : fill(t("drip.delete_in"), { d: days })}
    </span>
  );
}

function DripRow({ t, r, i }) {
  return (
    <div className="bg-white rounded-xl border border-app-border p-4 flex flex-wrap items-center gap-3" data-testid={`drip-row-${i}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-espresso truncate">{r.name || r.email}</div>
        <div className="text-xs text-espresso/50 truncate">{r.email}</div>
        <div className="text-[11px] text-espresso/60 mt-1">
          {t("drip.plan")}: <span className="font-medium">{r.plan === "none" ? t("drip.plan_none") : r.plan}</span>
          {" · "}
          {fill(t("drip.expired_for"), { d: r.days_expired })}
        </div>
      </div>
      <div className="flex flex-col items-end rtl:items-start gap-1 text-[11px] text-espresso/70">
        <span className="font-semibold text-espresso" data-testid={`drip-stage-${i}`}>
          {r.stage === 0 ? t("drip.stage_none") : fill(t("drip.stage_n"), { n: r.stage })}
        </span>
        {r.next_stage_in_days !== null && (
          <span className="inline-flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {fill(t("drip.next_in"), { d: r.next_stage_in_days })}
          </span>
        )}
        {r.last_sent && <span>{t("drip.last_sent")}: {fmtDate(r.last_sent)}</span>}
        {r.winback_code && (
          <span className="inline-flex items-center gap-1 font-mono" data-testid={`drip-code-${i}`}>
            <Ticket className="w-3 h-3" />
            {r.winback_code} ({r.winback_used ? t("drip.code_used") : t("drip.code_unused")})
            {r.reminder_sent && <span className="text-espresso/50">· {t("drip.reminded")}</span>}
          </span>
        )}
        <DeleteBadge t={t} days={r.days_until_delete} />
      </div>
    </div>
  );
}

export const ComebackReport = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/comebacks").then(({ data }) => setRows(data.comebacks || [])).catch(() => setRows([]));
  }, []);

  return (
    <section className="space-y-3" data-testid="comeback-report">
      <div>
        <h2 className="font-heading text-xl font-semibold text-espresso">{t("comeback.title")}</h2>
        <p className="text-xs text-espresso/60">{t("comeback.sub")}</p>
      </div>
      {rows === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-4" data-testid="comeback-empty">
          {t("comeback.empty")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="comeback-rows">
          {rows.map((r, i) => (
            <div key={r.id} className="bg-white rounded-xl border border-app-border p-3 flex flex-wrap items-center gap-3" data-testid={`comeback-row-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-espresso truncate">{r.name || r.email}</div>
                <div className="text-xs text-espresso/50 truncate">{r.email}</div>
                <div className="text-[11px] text-espresso/60 mt-0.5">
                  {t(`comeback.type_${r.type}`)} {r.stage > 0 && `· ${fill(t("comeback.after_stage"), { n: r.stage })}`}
                  {" · "}{t("comeback.returned")}: {fmtDate(r.returned_at)}
                </div>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  r.renewed ? "bg-green-100 text-green-700" : !r.still_exists ? "bg-gray-100 text-gray-500" : "bg-amber-100 text-amber-800"
                }`}
                data-testid={`comeback-status-${i}`}
              >
                {r.renewed ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                {r.renewed ? `${t("comeback.renewed")}${r.plan ? ` · ${r.plan}` : ""}` : !r.still_exists ? t("comeback.gone") : t("comeback.not_renewed")}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

function StatTile({ label, value, tone = "", testId }) {
  return (
    <div className={`bg-white rounded-xl border border-app-border px-4 py-3 ${tone}`} data-testid={testId}>
      <div className="text-2xl font-heading font-semibold text-espresso leading-none">{value}</div>
      <div className="text-[11px] text-espresso/60 mt-1">{label}</div>
    </div>
  );
}

function MonthStats({ t, s }) {
  if (!s) return null;
  return (
    <div className="space-y-1" data-testid="drip-month-stats">
      <div className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("drip.stats_title")} · {s.month}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <StatTile label={t("drip.stat_countdown")} value={s.in_countdown} testId="stat-countdown" />
        <StatTile label={t("drip.stat_warnings")} value={s.warnings_sent} testId="stat-warnings" />
        <StatTile label={t("drip.stat_reminders")} value={s.reminders_sent} testId="stat-reminders" />
        <StatTile label={t("drip.stat_deleted")} value={s.deleted} testId="stat-deleted" />
        <StatTile label={t("drip.stat_comebacks")} value={s.comebacks} testId="stat-comebacks" />
        <StatTile label={t("drip.stat_renewed")} value={s.renewed} tone="border-green-200 bg-green-50" testId="stat-renewed" />
      </div>
    </div>
  );
}

export const DripDashboard = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/renewal-drip");
      setData(d);
    } catch (_e) {
      void _e;
      setData({ rows: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: s } = await api.post("/admin/renewal-drip/run");
      toast.success(fill(t("drip.ran"), { sent: s.emails_sent, deleted: s.deleted }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setRunning(false);
  };

  return (
    <section className="space-y-3" data-testid="drip-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-espresso">{t("drip.title")}</h2>
          <p className="text-xs text-espresso/60">{t("drip.sub")}</p>
        </div>
        <Button size="sm" onClick={runNow} disabled={running} data-testid="drip-run-button">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />}
          {t("drip.run")}
        </Button>
      </div>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : data.rows.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-4" data-testid="drip-empty">
          {t("drip.empty")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="drip-rows">
          {data.rows.map((r, i) => <DripRow key={r.id} t={t} r={r} i={i} />)}
        </div>
      )}
      {data && <MonthStats t={t} s={data.month_stats} />}
    </section>
  );
};
